/**
 * useTour Hook
 * AIDLC Administrative Backend
 *
 * Manages the state of the interactive guided tour overlay.
 *
 * Responsibilities:
 * - Track current step index and active/inactive state
 * - Persist "tour completed" flag in localStorage so the tour
 *   auto-launches on the first visit but not on subsequent visits
 * - Provide methods: startTour, nextStep, prevStep, skipTour, goToStep
 *
 * The tour steps themselves are defined in GuidedTour.tsx since they
 * contain UI-specific content. This hook is purely state management.
 */

import { useState, useCallback, useEffect } from 'react';

const TOUR_STORAGE_KEY = 'aidlc-admin-tour-completed';

export interface UseTourReturn {
  /** Whether the tour overlay is currently visible */
  isActive: boolean;
  /** Zero-based index of the current step */
  currentStep: number;
  /** Total number of steps (set by the consumer) */
  totalSteps: number;
  /** Start the tour from step 0 */
  startTour: () => void;
  /** Advance to the next step. If on the last step, completes the tour. */
  nextStep: () => void;
  /** Go back one step. No-op if on step 0. */
  prevStep: () => void;
  /** End the tour immediately without completing all steps */
  skipTour: () => void;
  /** Jump to a specific step by index */
  goToStep: (step: number) => void;
  /** Complete the tour and persist the flag */
  completeTour: () => void;
  /** Whether this is the user's first visit (tour not yet completed) */
  isFirstVisit: boolean;
  /** Reset the stored flag so the tour will auto-launch again */
  resetTourFlag: () => void;
}

export function useTour(totalSteps: number): UseTourReturn {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  // On mount, check localStorage
  useEffect(() => {
    const hasCompleted = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!hasCompleted) {
      setIsFirstVisit(true);
      // Auto-launch tour on first visit after a brief delay
      // so the page has time to render
      const timer = setTimeout(() => {
        setIsActive(true);
        setCurrentStep(0);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev >= totalSteps - 1) {
        // Last step -- complete the tour
        setIsActive(false);
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        setIsFirstVisit(false);
        return 0;
      }
      return prev + 1;
    });
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setIsFirstVisit(false);
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step);
      }
    },
    [totalSteps]
  );

  const completeTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setIsFirstVisit(false);
  }, []);

  const resetTourFlag = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setIsFirstVisit(true);
  }, []);

  return {
    isActive,
    currentStep,
    totalSteps,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    goToStep,
    completeTour,
    isFirstVisit,
    resetTourFlag,
  };
}
